/**
 * Studio Create hub — pick a content type.
 * Routes like /studio/create/post?template=XYZ pass through to the
 * existing TemplateGallery filtered by aspect preset. For MVP we just
 * navigate to the gallery with a ?type= query param that the existing
 * gallery will display in a filter chip (next phase: proper filter).
 */
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Sparkles, Image as ImageIcon } from "lucide-react";
import { StudioShell, CONTENT_TYPES } from "./StudioShell";
import StudioInspirationStrip from "./StudioInspirationStrip";
import { useLanguage } from "../contexts/LanguageContext";

const TYPE_MAP = Object.fromEntries(CONTENT_TYPES.map(t => [t.key, t]));

export default function StudioCreate() {
  const { t } = useLanguage();
  const { type } = useParams();
  const navigate = useNavigate();

  // Deep-link flow: /studio/create/:type → forward to template gallery with a type filter
  if (type && TYPE_MAP[type]) {
    const typeCfg = TYPE_MAP[type];
    const typeLabel = (
      type === "post" ? (t.studio_create_type_post_label || "Post") :
      type === "story" ? (t.studio_create_type_story_label || "Story") :
      type === "reel" ? (t.studio_create_type_reel_label || "Reel") :
      type === "video" ? (t.studio_create_type_video_label || "Video") :
      type === "ad" ? (t.studio_create_type_ad_label || "Ad") :
      typeCfg.label
    );
    return (
      <StudioShell activeKey="/studio/create">
        <div data-testid={`studio-create-${type}`} style={{
          maxWidth: 840, margin: "0 auto", padding: "56px 20px 40px",
        }}>
          <button
            type="button"
            onClick={() => navigate("/studio/create")}
            style={{
              background: "transparent", border: 0, color: "#8b8ba7", fontSize: 13,
              cursor: "pointer", marginBottom: 20,
            }}
            data-testid="studio-create-back"
          >
            {t.studio_create_back_select || "← Back to selection"}
          </button>
          <h1 style={{
            color: "#fff", fontSize: "clamp(32px,4.6vw,52px)", fontWeight: 900,
            letterSpacing: "-0.02em", margin: "0 0 10px",
          }}>
            {t.studio_create_new_prefix || "New"} {typeLabel.toLowerCase()}
          </h1>
          <p style={{ color: "#a5a5c0", fontSize: 16, lineHeight: 1.6, margin: "0 0 32px" }}>
            {typeCfg.desc}. {t.studio_create_type_desc_suffix || "Pick a template, or start from a blank canvas."}
          </p>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            <CreateOption
              testid="studio-option-templates"
              title={t.studio_create_opt_templates_title || "Pick a template"}
              sub={t.studio_create_opt_templates_sub || "Hundreds of ready-made designs — editable"}
              accent={typeCfg.accent}
              onClick={() => navigate(`/ads-creator/templates?type=${type}`)}
            />
            <CreateOption
              testid="studio-option-blank"
              title={t.studio_create_opt_blank_title || "Blank canvas"}
              sub={t.studio_create_opt_blank_sub || "Full control, empty canvas"}
              accent="#334155"
              onClick={() => navigate(`/ads-creator/editor?type=${type}`)}
            />
            <CreateOption
              testid="studio-option-ai"
              title={t.studio_create_opt_ai_title || "AI Magic ✨"}
              sub={t.studio_create_opt_ai_sub || "Describe what you want — AI creates the first version"}
              accent="linear-gradient(135deg,#a855f7,#7c3aed)"
              onClick={() => navigate(`/ads-creator/instant-campaign?type=${type}`)}
            />
            <CreateOption
              testid="studio-option-photo"
              title={t.studio_create_opt_photo_title || "Start from Photo"}
              sub={t.studio_create_opt_photo_sub || "Upload your photo — AI writes copy that fits the image"}
              accent="linear-gradient(135deg,#ec4899,#a855f7)"
              onClick={() => navigate(`/ads-creator/photo-start?type=${type}`)}
            />
          </div>
        </div>
      </StudioShell>
    );
  }

  // Hub view — grid of content types
  return (
    <StudioShell activeKey="/studio/create">
      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "48px 20px 40px" }}>
        <div style={{ marginBottom: 28, textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "6px 14px", borderRadius: 999,
            background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.28)",
            color: "#c084fc", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase",
          }}>
            <Sparkles size={12} /> {t.studio_create_hub_eyebrow || "What are you creating?"}
          </div>
          <h1 style={{
            color: "#fff", fontSize: "clamp(36px,5vw,58px)", fontWeight: 900,
            letterSpacing: "-0.02em", margin: "16px 0 8px", lineHeight: 1.05,
          }}>
            {t.studio_create_hub_title_a || "Let's get"} <span style={{
              background: "linear-gradient(90deg,#a855f7,#7c3aed)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>{t.studio_create_hub_title_b || "creative"}</span>
          </h1>
          <p style={{ color: "#a5a5c0", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>
            {t.studio_create_hub_sub || "Pick a content type. Each preset opens the right dimensions and templates."}
          </p>
        </div>

        <div style={{
          display: "grid", gap: 16,
          gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
        }} data-testid="studio-create-grid">
          {CONTENT_TYPES.map((ct) => {
            const localLabel = (
              ct.key === "post" ? (t.studio_create_type_post_label || ct.label) :
              ct.key === "story" ? (t.studio_create_type_story_label || ct.label) :
              ct.key === "reel" ? (t.studio_create_type_reel_label || ct.label) :
              ct.key === "video" ? (t.studio_create_type_video_label || ct.label) :
              ct.key === "ad" ? (t.studio_create_type_ad_label || ct.label) :
              ct.label
            );
            const localDesc = (
              ct.key === "post" ? (t.studio_create_type_post_desc || ct.desc) :
              ct.key === "story" ? (t.studio_create_type_story_desc || ct.desc) :
              ct.key === "reel" ? (t.studio_create_type_reel_desc || ct.desc) :
              ct.key === "video" ? (t.studio_create_type_video_desc || ct.desc) :
              ct.key === "ad" ? (t.studio_create_type_ad_desc || ct.desc) :
              ct.desc
            );
            return (
            <button
              key={ct.key}
              type="button"
              onClick={() => navigate(ct.to)}
              data-testid={`studio-type-${ct.key}`}
              style={{
                padding: 20, textAlign: "left",
                background: "rgba(20,20,35,0.7)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 16, color: "#fff", cursor: "pointer",
                transition: "transform 160ms ease, border-color 160ms ease, background 160ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.borderColor = "rgba(168,85,247,0.35)";
                e.currentTarget.style.background = "rgba(30,20,40,0.85)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.borderColor = "";
                e.currentTarget.style.background = "";
              }}
            >
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: ct.accent, display: "inline-flex",
                alignItems: "center", justifyContent: "center",
                marginBottom: 12, color: "#fff",
              }}>
                <ct.icon size={24} />
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em", marginBottom: 4 }}>
                {localLabel}
              </div>
              <div style={{ fontSize: 13, color: "#a5a5c0", lineHeight: 1.4 }}>
                {localDesc}
              </div>
              <div style={{
                marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6,
                color: "#c084fc", fontSize: 12, fontWeight: 800,
              }}>
                {t.studio_create_card_start || "Start"} <ArrowRight size={14} />
              </div>
            </button>
          );})}
        </div>

        {/* V165 — Inspiration strip: scenarios, recents, trending */}
        <StudioInspirationStrip />
      </div>
    </StudioShell>
  );
}

function CreateOption({ title, sub, accent, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        padding: 18, textAlign: "left",
        background: "rgba(20,20,35,0.7)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 14, color: "#fff", cursor: "pointer",
        transition: "transform 160ms ease, border-color 160ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = "rgba(168,85,247,0.35)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderColor = ""; }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 10, marginBottom: 12,
        background: accent,
      }} />
      <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 13, color: "#a5a5c0" }}>{sub}</div>
    </button>
  );
}
